<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { NativeDateInput } from '#layers/base/public';
import type { ResolvedMonospaceApiKey, ResolvedMonospaceServiceAccount, ResolvedMonospaceUser } from '#layers/data-model/public';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import UserAvatar from '#layers/access/components/User/UserAvatar.vue';
import { useAccessDisplayNames } from '#layers/access/composables/use-access-display-names';
import { useApiKeyDialogs } from '#layers/access/composables/use-api-key-dialogs';
import { useApiKeys } from '#layers/access/composables/use-api-keys';
import { useOrganizationEntitlement } from '#layers/access/composables/use-organization-entitlement';
import { canCreateApiKey, canDeleteApiKey, canUpdateApiKey } from '#layers/access/constants/organization-entitlements';
import { useAuth } from '#layers/access/stores/auth';
import { formatExpiryLabel, formatInstantWithTimeZone, formatRelativeTime, useUserLocale } from '#layers/base/public';
import { hasPaginationContent, ListCard, OverflowMenu } from '#layers/ui/public';

interface ApiKeyListProps {
	apiKeys: ResolvedMonospaceApiKey[];
	totalItemsCount?: number;
	currentItemsCount?: number;
	isLoading?: boolean;
	isService?: boolean;
	hideCreate?: boolean;
	serviceAccount?: ResolvedMonospaceServiceAccount;
}

const props = defineProps<ApiKeyListProps>();

const pageNumber = defineModel<number>('page', { default: 1 });
const pageSize = defineModel<number>('pageSize', { default: 25 });

const { deactivate, revoke, reactivate, isExpired } = useApiKeys();
const { createApiKey, editApiKey } = useApiKeyDialogs();
const { can: canOrganization } = useOrganizationEntitlement();
const { currentUser } = storeToRefs(useAuth());

const canCreate = computed(() => canOrganization(canCreateApiKey));
const { getUserDisplayName } = useAccessDisplayNames();
const i18n = useI18n();
const { t } = i18n;
const { formatLocale } = useUserLocale();

function getIssuedAtBy(item: ResolvedMonospaceApiKey): string {
	const ago = formatRelativeTime(item.issuedAt, i18n, { locale: formatLocale.value, granularity: 'minute' });

	if (item.creator?.id === currentUser.value?.id) return t('api_keys.issued_ago_by_you', { ago });
	return t('api_keys.issued_ago_by', { ago, by: getUserDisplayName(item.creator as ResolvedMonospaceUser) });
}

function formatGeneralInstantTooltip(instant: NativeDateInput | null | undefined): string | undefined {
	if (!instant) return undefined;
	return formatInstantWithTimeZone(instant, { locale: formatLocale.value });
}

const headerCount = computed(() => props.totalItemsCount ?? props.currentItemsCount ?? props.apiKeys.length);
const headerText = computed(() => t('api_keys.count_header', { n: headerCount.value }));

const showPagination = computed(() => hasPaginationContent({
	page: pageNumber.value,
	pageSize: pageSize.value,
	totalItems: props.totalItemsCount,
	currentItems: props.currentItemsCount ?? props.apiKeys.length,
	loading: props.isLoading,
}));

async function handleDeactivate(apiKey: ResolvedMonospaceApiKey) {
	await deactivate(apiKey);
}

async function handleReactivate(apiKey: ResolvedMonospaceApiKey) {
	await reactivate(apiKey);
}

async function handleRevoke(apiKey: ResolvedMonospaceApiKey) {
	await revoke(apiKey);
}

function getCreateTarget() {
	// When scoped to a service account, link the new key to it directly.
	if (props.serviceAccount) return props.serviceAccount;
	// In the general service-key list, let the dialog prompt for a service account.
	if (props.isService) return;
	return currentUser.value as ResolvedMonospaceUser;
}

async function handleCreate() {
	await createApiKey(getCreateTarget());
}

// Guarded because row selection also routes here, not just the gated menu item.
async function handleEdit(apiKey: ResolvedMonospaceApiKey) {
	if (!canOrganization(canUpdateApiKey(apiKey.id))) return;
	await editApiKey(apiKey);
}

function getMenuItems(apiKey: ResolvedMonospaceApiKey): DropdownMenuItem[] {
	const items: DropdownMenuItem[] = [];

	if (canOrganization(canUpdateApiKey(apiKey.id))) {
		items.push({
			label: t('with_ellipsis', { string: t('api_keys.edit') }),
			icon: 'lucide:pencil',
			onSelect: () => handleEdit(apiKey),
		});

		if (apiKey.active) {
			items.push({
				label: t('api_keys.deactivate'),
				icon: 'lucide:x-circle',
				onSelect: () => handleDeactivate(apiKey),
			});
		}
		else {
			items.push({
				label: t('api_keys.reactivate'),
				icon: 'lucide:check-circle',
				onSelect: () => handleReactivate(apiKey),
			});
		}
	}

	if (canOrganization(canDeleteApiKey(apiKey.id))) {
		items.push({
			label: t('with_ellipsis', { string: t('api_keys.revoke') }),
			icon: 'lucide:trash-2',
			color: 'error',
			onSelect: () => handleRevoke(apiKey),
		});
	}

	return items;
}

const menuItemsById = computed(() => new Map(props.apiKeys.map((apiKey) => [apiKey.id, getMenuItems(apiKey)])));

function menuItemsFor(apiKey: ResolvedMonospaceApiKey): DropdownMenuItem[] {
	return menuItemsById.value.get(apiKey.id) ?? [];
}
</script>

<template>
	<DSkeleton v-if="isLoading && headerCount === 0" class="h-14 w-full" />
	<ListCard
		v-else
		data-testid="api-key-list"
		:items="apiKeys"
		selectable
		size="sm"
		:ui="{
			item: {
				base: 'cursor-pointer',
				label: 'flex items-center gap-2 font-medium',
				leading: 'flex size-8 items-center justify-center rounded-full bg-muted',
				trailing: 'flex items-center gap-2',
				description: 'flex flex-col gap-1 text-dimmed',
			},
		}"
		@select="(item) => handleEdit(item as ResolvedMonospaceApiKey)"
	>
		<template #header>
			{{ headerText }}
		</template>

		<template #header-actions>
			<DButton
				v-if="!hideCreate && canCreate"
				color="neutral"
				variant="outline"
				size="sm"
				:label="$t('api_keys.create_title')"
				@click="handleCreate"
			/>
		</template>

		<template #item-leading="{ item }">
			<UserAvatar v-if="item.user" :user="item.user" class="size-8" :class="{ 'opacity-50': isExpired(item) }" />
		</template>

		<template #item-label="{ item }">
			<span :class="{ 'opacity-50': isExpired(item) }">{{ item.name }}</span>
			<template v-if="isService && item.user?.fullName">
				<span>&middot;</span>
				<span class="text-dimmed">{{ item.user.fullName }}</span>
			</template>
		</template>

		<template #item-description="{ item }">
			<template v-if="item.description">
				<div>
					<span class="text-muted">{{ item.description }}</span>
				</div>
			</template>
			<div class="flex items-start gap-1">
				<DTooltip :text="formatGeneralInstantTooltip(item.expiresAt)">
					<span>{{ formatExpiryLabel(item.expiresAt, i18n, formatLocale) }}</span>
				</DTooltip>
				<span>&middot;</span>
				<DTooltip :text="formatGeneralInstantTooltip(item.issuedAt)">
					<span>{{ getIssuedAtBy(item) }}</span>
				</DTooltip>
			</div>
		</template>

		<template #item-trailing="{ item }">
			<DBadge v-if="!item.active" color="error" variant="soft" size="md">
				{{ $t('api_keys.inactive') }}
			</DBadge>

			<OverflowMenu v-if="menuItemsFor(item).length > 0" :items="menuItemsFor(item)" />
		</template>

		<template v-if="showPagination" #footer>
			<DPaginationBar
				v-model:page="pageNumber"
				v-model:page-size="pageSize"
				:current-items="currentItemsCount ?? apiKeys.length"
				:total-items="totalItemsCount"
				:loading="isLoading"
			/>
		</template>
	</ListCard>
</template>
